import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { AgentTaskStatus, DigestedTaskStatus, FullDigestedTask } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { revisionAtLeast, type TaskDisplayStatus } from '@wix/ai-scheduling-assistant-common-lib/utils';
import type { PostPlan, PublishedPost, TaskDecisionPrompt } from '@wix/ai-scheduling-assistant-common-lib/utils';
import type { SocialPostPreviewCardMetadata } from '@wix/ai-scheduling-assistant-common-lib/entities';
import type { IslandOutreachQuestion } from './island-quickview';
import { parseOpenAsks, type OpenAsksRead } from './open-asks';
import { IndividualsDuplexerChannel } from '@wix/ai-scheduling-assistant-common-lib/commonTypes';
import { channelListenerCount } from './duplexer-connection';
import { queryClient, queryKeys } from './query-client';
import { noteFetch } from './runaway-fetch-probe';
import { invalidateAgentTaskQueries, useAgentTaskInvalidation } from './agent-task-invalidation';
import { isSettled } from './agent-task-buckets';
import type { SentOutreachSummary } from './outreach-rows';
import { cachedOutreachPayload, mergeOutreaches, startOutreachRefresh } from './outreaches';
import { withGoOutAt } from './task-schedule';
import { marketingPublishTimeEnabled } from './marketing-publish-time-flag';


// On the v1 path this is the inner FullDigestedTask + the digest id. On the v2 (AgentTask) path the
// digest-only fields (meetingDetails / taskType / taskProgress) are absent and `status` may be any of
// the 10 AgentTaskStatus values; `currentAction` / `summary` carry the lean AgentTask display. The
// task's Master Plan is flattened onto `plan` as a bare step array on both paths (v1 from
// FullDigestedTask.plan.steps, v2 parsed from the AgentTask's display.plan) — `[]` when there is none.
export type WebTask = Omit<FullDigestedTask, 'status' | 'plan'> & import('./task-content-fields').TaskContentFields & {
  digestId: string;
  /** The SDL revision — monotonic per task, and the only thing that ORDERS two reads of it. See
   *  `replaceTask`, which refuses a row older than the one already cached. Absent = "accept". */
  revision?: string;
  /** The server's derived verdict. Read by every bucket / liveness / quiet-state helper BEFORE `status`,
   *  which is why `patchTaskInStore` drops it when it writes a status locally. Absent = derive it. */
  displayStatus?: TaskDisplayStatus;
  status: DigestedTaskStatus | AgentTaskStatus;
  currentAction?: string;
  summary?: string;
  plan: { title: string; completed: boolean }[];
  /** The Space (workspace) the task belongs to — carried so the cross-workspace Tasks list can show a
   *  per-card Space indicator and filter by Space. Empty on the v1 digest path / unscoped tasks. */
  workspaceId?: string;
  /** The agent the task is assigned to — carried so the per-agent Tasks list (on the agent screen) can
   *  filter to that agent's tasks. Empty on the v1 digest path. */
  agentId?: string;
  /** Files the task produced (VFS paths) — the outcome line on a completed card. Absent on the v1 path. */
  artifacts?: string[];
  /** The display subtitle the agent wrote alongside the title — the other half of that outcome line. */
  subtitle?: string;
  /** The agent's own working conversation, and the reviewer's when the task is in review. Both are carried
   *  because a task in review has its activity on the REVIEWER conversation, so "is it actually running"
   *  cannot be answered from either alone. Distinct from `conversationId`, the chat-handoff target. */
  agentConversationId?: string;
  reviewerConversationId?: string;
  reviewerAgentId?: string;
  reviewStage?: string;
  /** Is an agent loop executing on this task right now, and when did it last do anything? Stamped by the
   *  route's conversation join for the unsettled tasks only — absent means "not computed", which
   *  `isInMotion` must never read as "not running". Feeds the island count and the card's live marker. */
  runInFlight?: boolean;
  lastActivityAt?: string;
  /** A COMPLETED task's finish, from its change log's latest entry (`/api/tasks`); `null` when a sweep closed it. */
  finishedAt?: string | null;
  /**
   * The open questions this task is blocked on, stamped by `/api/tasks`'s decision join. **A tri-state
   * with `decisionPromptsUnavailable`, and collapsing any two of them is the defect** (see
   * `TaskDecisionPrompts.ts` in common-lib): absent-and-not-unavailable means there is no open question,
   * a non-empty array means these are the questions, and `decisionPromptsUnavailable` means the task
   * NAMES a blocker we could not read. Never `[]`, never `false`.
   */
  decisionPrompts?: TaskDecisionPrompt[];
  decisionPromptsUnavailable?: boolean;
  /** The task's drafted social post, in every state — `/api/tasks` stamps it (see the route's `WebTask`). The row
   *  and the island card draw it above the question only while that question is open (`postPreviewHoldsDecision`);
   *  the Calendar draws it whatever the post's state. Absent = none. */
  socialPostPreviewCard?: SocialPostPreviewCardMetadata;
  /** The planned post's idea before it is drafted (`display.postPlan`). Absent = none. */
  postPlan?: PostPlan;
  /** The post once it went out (`display.publishedPost`). Absent = not published. */
  publishedPost?: PublishedPost;
  /** `display.origin` — `saved-action` on a post approved ahead. Read by `postStateOf`. */
  displayOrigin?: string;
  /** The task's decision edges and the answer stamped back on each, for `postStateOf`. Absent = none. */
  blockedByDecisions?: { decisionId: string; resolvedChoice?: string }[];
  /** The instant the agent may not start before, ISO. A PENDING task carrying a FUTURE one is
   *  "scheduled" — derived, never a status (see lib/task-schedule.ts). Absent on everything that runs now. */
  notBefore?: string;
  /** When a marketing-plan post goes out, ISO (`display.marketingPlanPost.postAt`) — the date the owner sees;
   *  its `notBefore` is the day before, when the agent starts preparing it. Absent on everything else. */
  postAt?: string;
  deliveryAt?: string;
  /** The tasks this one waits for, joined by the route (id/title/status/date). Present only when it has
   *  blockers. Read by the shared gated rule, `lib/task-schedule.ts`. */
  blockedByTasks?: import('./task-schedule').TaskBlockerRef[];
  /** The goal whose plan laid this task out — the task's ORIGIN, the counterpart of `pulseIdentityId`,
   *  and what lets the row and the detail say where the task came from. Absent on unaffiliated work. */
  goalId?: string;
  /** That goal's title, joined by the route (see `/api/tasks`) so a row can NAME its origin without a
   *  per-Space goals fetch. Absent when the goal could not be read — the row then names nothing. */
  goalTitle?: string;
  /** This task tracks a dashboard BUILD, which runs service-side with no agent conversation behind it
   *  (see the harness's `dashboard-build-task.ts`). The detail drill-in reads it to say so, instead of
   *  promising a transcript that will never arrive. */
  isDashboardBuild?: boolean;
  /** The build's marker: the dashboard it targets and, once the run ended, what it produced (`lib/dashboard-build.ts`). */
  dashboardBuild?: import('./dashboard-build').DashboardBuildMarker;
  /** Present only on a row that is NOT an AgentTask — an inbound outreach with no task of its own.
   *  The row renders like any other; this tells the card a click opens the conversation the user's
   *  own agent presented it in, since there is no task detail to show. */
  rowKind?: 'outreach';
  /** Who sent it, for an outreach row's title. */
  senderName?: string;
  /**
   * The FILE this outreach announces, when it announces one rather than carrying something a person
   * wrote — `Outreach.shared_file`, set by `ShareFile`'s own notification.
   *
   * Its presence is what makes the row a SHARE rather than a message, and that distinction is the whole
   * point: without it the row can only render as correspondence, under a Reply button, which is the one
   * action that cannot open the file the reader came for. Absent on every ordinary outreach.
   *
   * `fileId` is a handle resolved through the reader's own grant at open time (`/api/shared-file`), so a
   * revoked or deleted file says so when they tap rather than being stale here.
   */
  sharedFile?: { fileId: string; fileName?: string; shareLink?: string };
  /** The whole thread, oldest first. An outreach row opens IN PLACE — the conversation it was
   *  delivered into is not in the user's chat list, so navigating to it lands on the root. */
  outreachMessages?: { text: string; at?: string; relative?: string; mine: boolean }[];
  /** Outreaches this task sent — who it reached, WHAT IT SAID (`messages`, the whole correspondence),
   *  and whether they ever answered. One shared type with the server's own, so a field added there
   *  cannot go unparsed here. */
  sentOutreaches?: SentOutreachSummary[];
};

export type TasksState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; tasks: WebTask[] };

export type TaskBucket = DigestedTaskStatus.ACTION_NEEDED | DigestedTaskStatus.IN_PROGRESS | DigestedTaskStatus.COMPLETED;

export type TasksByBucket = Record<TaskBucket, WebTask[]>;

// Mirrors mobile's categorizeTasksByStatus: PENDING and ACTION_NEEDED fold
// into one user-facing "action needed" pile, PAUSED + IN_PROGRESS + WAITING
// (+ the v2 IN_REVIEW) fold into one "in progress" pile, COMPLETED stays on
// its own. The v2-only terminal CANCELLED joins the completed pile; the v1
// digest status set never produces CANCELLED/IN_REVIEW, so this is a no-op
// there.
export function categorizeTasksByStatus(tasks: WebTask[]): TasksByBucket {
  const buckets: TasksByBucket = {
    [DigestedTaskStatus.ACTION_NEEDED]: [],
    [DigestedTaskStatus.IN_PROGRESS]: [],
    [DigestedTaskStatus.COMPLETED]: [],
  };
  for (const task of tasks) {
    const bucket: TaskBucket =
      task.status === DigestedTaskStatus.COMPLETED || task.status === AgentTaskStatus.CANCELLED
        ? DigestedTaskStatus.COMPLETED
        : task.status === DigestedTaskStatus.ACTION_NEEDED || task.status === DigestedTaskStatus.PENDING
        ? DigestedTaskStatus.ACTION_NEEDED
        : DigestedTaskStatus.IN_PROGRESS;
    buckets[bucket].push(task);
  }
  return buckets;
}

/**
 * How often an unsettled list re-reads itself. Slow enough to be a safety net rather than a poll loop:
 * the duplexer push is still what makes a change feel instant, and this only bounds how wrong the pill
 * can be when a push does not arrive.
 */
const UNSETTLED_POLL_MS = 15_000;

/**
 * Whether anything in the list can still change on its own. A finished list needs no timer.
 *
 * **One definition of finished, shared with the bucketing.** A FAILED or MOOT task is terminal and
 * UN-CLOSEABLE, so a status list that missed either would pin a 15 s poll of `/api/tasks` on forever, on
 * EVERY route, because the island pill mounts on all of them.
 *
 * Deriving it from `isSettled` rather than restating the statuses is the point — a fifth terminal status
 * added to `bucketContaining` can no longer leave a poll running behind it.
 */
function hasUnsettled(tasks: WebTask[] | undefined): boolean {
  return (tasks ?? []).some((task) => !isSettled(task));
}

/**
 * Whether the Maestro is running a non-task job right now — the `activeExternalJobs` (Wixel render)
 * folded into the island's blue count. It lives in its OWN cache entry (`fetchTasks` writes it as a
 * sibling of the rows, see below), so `hasUnsettled` — which only sees the task array — cannot. Without
 * this a render the Maestro runs ITSELF lights the blue pill and, being no task, keeps the poll off, so
 * the pill stayed blue until an unrelated event: the exact stuck-island bug. A sub-agent's render is a
 * real task, so `hasUnsettled` already keeps its poll alive. Absent ≡ empty (never a poll from ignorance).
 */
function hasActiveMaestroAction(): boolean {
  return (queryClient.getQueryData<MaestroAction[]>(queryKeys.maestroActions) ?? []).length > 0;
}

/**
 * The floor under the push, as an option any observer of a tasks list can take. Exported because the
 * island quick-view reads the SAME account-wide entry (see `use-island-quickview`) and is the reader
 * mounted on every screen — a floor that only the `/tasks` screen carries would leave the most visible
 * surface the stalest one, which is the defect the timer was added for. A live Maestro action keeps it
 * running too: the poll that re-reads it clear is what takes the blue pill down when the render ends.
 */
export const unsettledTasksRefetchInterval = (tasks: WebTask[] | undefined): number | false =>
  hasUnsettled(tasks) || hasActiveMaestroAction() ? UNSETTLED_POLL_MS : false;

/**
 * How often the ACCOUNT-WIDE entry re-reads itself when nothing is unsettled. That entry carries more than the
 * rows: the open asks and the outreach-only questions behind the "Action needed" number change with no task
 * changing at all (a new ask, one the ledger expired, one resolved by a reply on another surface), and no push
 * reaches the browser for them. Without this floor an idle tab kept a stale number indefinitely while the phones
 * and the Watch moved on. Foreground only (`refetchIntervalInBackground: false`), and the entry also re-reads
 * on window focus.
 */
const ACTION_NEEDED_IDLE_POLL_MS = 120_000;

export const accountTasksRefetchInterval = (tasks: WebTask[] | undefined): number =>
  unsettledTasksRefetchInterval(tasks) || ACTION_NEEDED_IDLE_POLL_MS;

/**
 * One "Maestro action" the island renders as a working row that is not a task: a delegated external job
 * (a Wixel render) or a live free-run agent loop with no task row behind it. Served by `/api/tasks` as a
 * sibling of the rows (account-wide reads only — an action is account-level work and belongs to no Space).
 * Same shape as the server aggregate's `maestroActions`, so the three surfaces parse one contract.
 */
export interface MaestroAction {
  kind: string;
  label?: string;
  startedAt?: string;
  taskId?: string;
  agentId?: string;
}

function optionalId(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/** The wire field, read defensively: absent (an older server) ≡ empty, and a malformed entry (no string
 *  `kind`) is dropped rather than trusted — never over-claim work from a shape we don't recognize. */
function parseMaestroActions(raw: unknown): MaestroAction[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((entry): entry is MaestroAction => {
      const candidate = entry as { kind?: unknown } | null;
      return !!candidate && typeof candidate === 'object' && typeof candidate.kind === 'string';
    })
    .map(({ taskId, agentId, ...rest }) => {
      const task = optionalId(taskId);
      const agent = optionalId(agentId);
      return { ...rest, ...(task ? { taskId: task } : {}), ...(agent ? { agentId: agent } : {}) };
    });
}

/**
 * The OUTREACH-ONLY open questions the account-wide `/api/tasks` read carries beside its rows
 * (`outreachQuestions` + `outreachQuestionsTotal`, outreach visibility phase 5) — questions raised on a thread
 * with no task, goal or meeting behind them, which the island shows as their own cards. `null` = the field was
 * absent (an old server, or the server could not look): no outreach cards, nothing added — never "none open"
 * asserted from ignorance, and never a pill hidden over it either.
 */
export interface OutreachQuestionsRead {
  questions: IslandOutreachQuestion[];
  /** How many are open in all; the list may be capped below it. Absent → the list's own length. */
  total?: number;
}

/** The two wire fields, read defensively: absent → `null`; a malformed entry (no string `decisionId` /
 *  `outreachId`) is dropped rather than trusted. */
export function parseOutreachQuestions(raw: unknown, total: unknown): OutreachQuestionsRead | null {
  if (!Array.isArray(raw)) return null;
  const questions = raw.filter((entry): entry is IslandOutreachQuestion => {
    const candidate = entry as { decisionId?: unknown; outreachId?: unknown } | null;
    return (
      !!candidate &&
      typeof candidate === 'object' &&
      typeof candidate.decisionId === 'string' &&
      !!candidate.decisionId &&
      typeof candidate.outreachId === 'string' &&
      !!candidate.outreachId
    );
  });
  return typeof total === 'number' && Number.isFinite(total) ? { questions, total } : { questions };
}

/**
 * The list fetch behind every scope. Exported so the island quick-view can subscribe to the SAME
 * account-wide cache entry the Tasks screen renders from — one query key, one queryFn, one set of rows —
 * with its own always-mounted refetch policy (see `use-island-quickview`).
 */
export async function fetchTasks(workspaceId?: string): Promise<WebTask[]> {
  // Every mounted useTasks registers an agent-task listener that refetches THIS entry, and one
  // duplexer event runs them all in a single tick — so the listener count is the burst's own
  // multiplier, and the only figure a stack trace cannot supply.
  noteFetch('tasks', workspaceId ?? 'account-wide', () => `subs=${channelListenerCount(IndividualsDuplexerChannel.AGENT_TASK)}`);
  // Started BEFORE the task read, not after it: in flight beside the task read it costs the list nothing
  // at all. The rows are merged into this very cache entry when they land (see `lib/outreaches.ts`).
  const pendingOutreaches = startOutreachRefresh();
  const query = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
  const [res, publishTime] = await Promise.all([apiFetch(`${API_BASE}/api/tasks${query}`, { credentials: 'include' }), marketingPublishTimeEnabled()]);
  if (!res.ok) {
    throw new Error(`tasks: ${res.status}`);
  }
  const data = (await res.json()) as {
    tasks?: WebTask[];
    maestroActions?: unknown;
    outreachQuestions?: unknown;
    outreachQuestionsTotal?: unknown;
    openAsks?: unknown;
    openAsksTotal?: unknown;
  };
  // The maestro actions ride the account-wide response and land in their own cache entry, OVERWRITTEN on
  // every fetch — a poll that stops returning an action is what drops its row. Only the account-wide read
  // writes it: the scoped route doesn't carry the field, and letting a Space-scoped fetch clear it would
  // blank the island's action rows every time a workspace screen refreshed.
  if (!workspaceId) {
    queryClient.setQueryData<MaestroAction[]>(queryKeys.maestroActions, parseMaestroActions(data.maestroActions));
    // The island's outreach-only question cards, on the same rule: every account-wide read overwrites them
    // (an answered question leaves on the re-read the answer's `['tasks']` invalidation triggers), and a
    // scoped read never touches them.
    queryClient.setQueryData<OutreachQuestionsRead | null>(
      queryKeys.outreachQuestions,
      parseOutreachQuestions(data.outreachQuestions, data.outreachQuestionsTotal),
    );
    // The open asks no task row or outreach question carries (`lib/open-asks.ts`), on the same overwrite rule.
    queryClient.setQueryData<OpenAsksRead | null>(queryKeys.openAsks, parseOpenAsks(data.openAsks, data.openAsksTotal));
  }
  const tasks = (data.tasks ?? []).map((task) => withGoOutAt(task, publishTime));

  // The payload only FOLDS the sent outreaches onto their tasks now — inbound rows live on the Outreach
  // screen, not in this list — so there is nothing to wait for: an empty task list is an empty task list,
  // and a failed outreach read can no longer be mistaken for "no messages" here. The fold lands when it
  // lands (`refreshOutreaches` patches this entry).
  void pendingOutreaches;
  const cached = cachedOutreachPayload();
  return cached ? mergeOutreaches(tasks, cached, workspaceId) : tasks;
}

/** Warm the account-wide task list into the React Query cache on app mount, so the Your Agents summary
 *  tile doesn't sit on a spinner. Best-effort; swallows errors. */
export function prefetchTasks(): void {
  void queryClient.prefetchQuery({ queryKey: queryKeys.tasks(undefined), queryFn: () => fetchTasks(undefined) }).catch(() => {});
}

/**
 * The Agent Tasks list. `workspaceId` scopes it to that workspace + its child projects (the workspace's
 * "Agents at work" links in); omitted, it's the account-wide list the top-level Tasks nav entry shows.
 *
 * `enabled: false` only pauses fetching — a cached list for that key is still served, so a caller that
 * must show nothing until it knows its scope has to suppress the state itself (see useScopedTasks).
 */
export function useTasks(workspaceId?: string, options?: { enabled?: boolean }): {
  state: TasksState;
  refresh: () => Promise<void>;
} {
  const enabled = options?.enabled ?? true;
  const query = useQuery({
    queryKey: queryKeys.tasks(workspaceId),
    queryFn: () => fetchTasks(workspaceId),
    enabled,
    // A FLOOR under the push, not a replacement for it. Freshness here rides a four-hop chain — the
    // service writes, a domain event is produced, the harness consumer reads it, and only then is a
    // duplexer frame pushed to this browser — and a list with no timer of its own is exactly as stale
    // as the unluckiest hop.
    //
    // Only while something is unsettled: a list of finished tasks cannot change on its own, so a
    // settled list costs nothing. Foreground only, and React Query dedupes by key, so the several
    // components reading the same scope share one request rather than multiplying it.
    refetchInterval: (data) =>
      workspaceId ? unsettledTasksRefetchInterval(data as WebTask[] | undefined) : accountTasksRefetchInterval(data as WebTask[] | undefined),
    refetchIntervalInBackground: false,
    // The account-wide entry carries the Action needed asks, which change without a push: re-read on return.
    refetchOnWindowFocus: !workspaceId,
  });

  // The push, through the ONE shared agent-task listener (`lib/agent-task-invalidation.ts`). Invalidation
  // drives each active key once; a disabled query is left alone.
  useAgentTaskInvalidation();

  const refresh = async () => {
    await query.refetch();
  };

  // Keep a prior good list on a transient refetch failure — only surface an error before the first success.
  if (query.data) return { state: { kind: 'ready', tasks: query.data }, refresh };
  if (enabled && query.status === 'error') {
    return { state: { kind: 'error' }, refresh };
  }
  return { state: { kind: 'loading' }, refresh };
}

/**
 * Apply `update` to a task across EVERY cached scope (account-wide and each workspace-scoped list), so a
 * mutation fired from one screen can't leave another showing the stale status. Returns the pre-edit task
 * for rollback, or null when it wasn't cached anywhere.
 */
function updateTaskInCache(id: string, update: (task: WebTask) => WebTask): WebTask | null {
  let previous: WebTask | null = null;
  queryClient.setQueriesData<WebTask[]>({ queryKey: queryKeys.tasksAllScopes }, (tasks) =>
    tasks?.map((task) => {
      if (task.id !== id) return task;
      previous = task;
      return update(task);
    }),
  );
  return previous;
}

/**
 * Replace a cached task wholesale — with the ordering guard.
 *
 * Every writer here is unordered with respect to the others: a list refresh, a duplexer-driven re-read,
 * a mutation echo and a per-card poll land whenever they land. So a read ISSUED before a task finished
 * can resolve after it and put "In progress" back over "Done". `revisionAtLeast` is the one rule that
 * orders two reads of a task (iOS's at-work card already guards this locally with a monotonic read
 * counter — same problem, solved once for one card).
 *
 * Absent revisions compare as "accept", so nothing changes where the data cannot say.
 *
 * **A rollback is exempt.** It restores a row this client captured BEFORE its own optimistic write, so by
 * construction it carries the older revision and the guard would refuse exactly the restore that has to
 * happen — leaving a failed mutation's optimistic status on screen for good.
 */
function replaceTask(id: string, replacement: WebTask, opts?: { rollback?: boolean }): void {
  updateTaskInCache(id, (held) => {
    if (opts?.rollback) return replacement;
    return revisionAtLeast(replacement.revision, held.revision) ? replacement : held;
  });
}

/**
 * Patch fields onto a cached task — and DROP the server's verdict when the patch moves the status.
 *
 * Every bucket / liveness / quiet-state helper reads `displayStatus` BEFORE it looks at `status`, which
 * is the whole point of the field. So an optimistic `status: CANCELLED` with the old verdict still
 * attached renders the OLD bucket and the old spinner — the local write appears to do nothing until a
 * refetch lands. Dropping the verdict hands the row back to the client's own rule, which reads the
 * status we just wrote; the next payload re-attaches the server's.
 *
 * Caught in review. The same applies to the native cancel/stop paths, which copy the status onto the row.
 */
function patchTaskInStore(id: string, patch: Partial<WebTask>): WebTask | null {
  const dropsVerdict = 'status' in patch && !('displayStatus' in patch);
  return updateTaskInCache(id, (task) => {
    const next = { ...task, ...patch };
    if (dropsVerdict) delete next.displayStatus;
    return next;
  });
}

/**
 * The user calls a task off. `/api/tasks/:id/cancel` proxies the native-data `task-cancel` action and
 * echoes a bare `AgentTask`, so there is nothing here to reshape and the reconcile is the echoed status
 * alone.
 *
 * Optimistic + rollback, the house style: patch every cached scope to CANCELLED, reconcile from the
 * echo, restore the pre-edit task on any failure. The open detail modal reads a different cache entry
 * (`['task-detail', id]`), so it is patched alongside — without it the list row would read Cancelled
 * while the sheet the user is looking at still said In progress.
 */
export async function cancelTask(taskId: string): Promise<{ ok: boolean }> {
  const previous = patchTaskInStore(taskId, { status: AgentTaskStatus.CANCELLED });
  const previousDetail = patchTaskDetailInCache(taskId, AgentTaskStatus.CANCELLED);
  const rollback = () => {
    if (previous) replaceTask(taskId, previous, { rollback: true });
    if (previousDetail !== null) patchTaskDetailInCache(taskId, previousDetail);
  };
  try {
    const res = await apiFetch(`${API_BASE}/api/tasks/${encodeURIComponent(taskId)}/cancel`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) {
      rollback();
      return { ok: false };
    }
    const data = (await res.json()) as { task?: { status?: string } | null };
    const status = data.task?.status;
    if (status) {
      patchTaskInStore(taskId, { status: status as AgentTaskStatus });
      patchTaskDetailInCache(taskId, status);
    }
    // The optimistic patches above cover the two caches whose rows are ON SCREEN when the user confirms
    // (the list they clicked from, and the sheet they are looking at) — they exist so neither flickers.
    // Everything else holding this task re-reads from the ONE shared set rather than being enumerated
    // here: the in-chat at-work card, the workspace counts, the agent transcript.
    //
    // An AGENT_TASK push does eventually reach most of them — the harness republishes the domain event
    // (`publishAgentTaskEvent`) and the shared listener refetches. This does not wait for that Kafka round
    // trip, and more importantly it does not DEPEND on it: convergence after our own write should not rest
    // on a consumer that runs only on prod pods. The enumeration was the real defect — every mutation path
    // listed a different subset, and `['task-detail', id]` had no push of its own at all.
    invalidateAgentTaskQueries(taskId);
    return { ok: true };
  } catch {
    rollback();
    return { ok: false };
  }
}

/** Writes `status` onto the single-task detail entry, returning the one it replaced (null if uncached). */
function patchTaskDetailInCache(taskId: string, status: string): string | null {
  let previous: string | null = null;
  queryClient.setQueryData<{ status?: string; displayStatus?: unknown }>(queryKeys.taskDetail(taskId), (task) => {
    if (!task) return task;
    previous = task.status ?? '';
    // Same reason as `patchTaskInStore`: the verdict outranks `status` at every read site, so leaving a
    // stale one attached makes the sheet keep its old bucket and spinner after the user cancelled.
    const { displayStatus: _dropped, ...rest } = task;
    return { ...rest, status };
  });
  return previous;
}
